import CTNDropdown from '@ctn/ui/components/ctn-dropdown';
import CTNModal from '@ctn/ui/components/ctn-modal';
import CTNUpload from '@ctn/ui/components/ctn-upload';
import { type CTNDropdownOptionProps } from '@ctn/ui/types/ctn-dropdown';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useForm, useFormContext, type Resolver } from 'react-hook-form';
import * as yup from 'yup';

import { useGlobalContext } from '@/context/global.context';
import { useFileDuplicate } from '@/hooks/useFileDuplicate';

import ERROR_MESSAGE from '@/constants/errors.constants';
import { getLookup } from '@/helpers/dropdown';
import { formatDoc } from '@/helpers/formSolicitudes';
import { commonUpload, getConfigUpload } from '@/helpers/upload';
import { peekValidation } from '@/helpers/validations';
import { documentoLimitadoSchema } from '@/helpers/validationsDocuments';
import { type FormValues as FormValuesGlobal, type IDocumentoForm } from '@/interfaces/model/don.interface';
import { type TipoDocumento } from '@/interfaces/model/maestros.interface';
import type { CTNUploadRef, FileUpload } from '@ctn/ui/types/ctn-upload';

type ModalParams = {
	readonly isOpen: boolean;
	readonly title: string;
	readonly allDocuments: IDocumentoForm[];
	readonly setIsOpen: (param: boolean) => void;
	readonly onSave: (param: IDocumentoForm) => void;
	readonly availablesDocumentos: TipoDocumento[];
};

type FormValues = {
	idTipoDocumento: string;
	documento: IDocumentoForm;
};

const schema = ({ globalValues, maxSizeAllDoc }: { globalValues: FormValuesGlobal; maxSizeAllDoc: number }) =>
	yup.object().shape({
		idTipoDocumento: yup.string().required(ERROR_MESSAGE.required),
		documento: documentoLimitadoSchema({ maxSizeAllDoc, globalValues }).required(ERROR_MESSAGE.docRequired),
	});

export const resolver = ({
	globalValues,
	maxSizeAllDoc,
}: {
	globalValues: FormValuesGlobal;
	maxSizeAllDoc: number;
}): Resolver<any, string> => yupResolver(schema({ globalValues, maxSizeAllDoc }));

const ModalAddDocumento = ({
	isOpen,
	setIsOpen,
	allDocuments,
	availablesDocumentos,
	title,
	onSave,
}: ModalParams): ReactElement => {
	const refFile = useRef<CTNUploadRef>(null);
	const fileDuplicate = useFileDuplicate(refFile);
	const {
		maestrosTramite: { tipoDocumento, configuracionTramite },
	} = useGlobalContext();
	const [hasMissingRequired, setHasMissingRequired] = useState(true);

	const getOptionsMaestro = (maestros: TipoDocumento[]): CTNDropdownOptionProps[] =>
		maestros.map(({ id, descripcion, minimoObligatorio }) => {
			const textoDocumentos = minimoObligatorio === 1 ? 'Documento' : 'Documentos';
			const label =
				minimoObligatorio > 0
					? `${descripcion} (${minimoObligatorio} ${textoDocumentos})`
					: descripcion;
			return { value: String(id), label };
		});

	const options: CTNDropdownOptionProps[] = getOptionsMaestro(availablesDocumentos);
	const hasMultipleOptions = options.length > 1;

	const { watch: watchGlobal } = useFormContext<FormValuesGlobal>();

	const {
		control,
		formState: { errors },
		setValue,
		watch,
		reset,
		trigger,
	} = useForm<Partial<FormValues>>({
		mode: 'onSubmit',
		resolver: resolver({
			globalValues: watchGlobal(),
			maxSizeAllDoc: configuracionTramite?.pesoMaximoSolicitud ?? 0,
		}),
	});

	const { documento, idTipoDocumento } = watch();

	const handleSave = async () => {
		const isValid = await trigger();
		if (!isValid || !documento || !idTipoDocumento) return;
		onSave({ ...documento, tipoDocumento: getLookup(idTipoDocumento, tipoDocumento) as TipoDocumento });
		closeModal();
	};

	const closeModal = () => {
		setIsOpen(false);
	};

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'tertiary',
			mode: 'tertiary',
			label: 'Cancelar',
			size: 'size-40',
			minWidth: '140',
			onClick: closeModal,
		},
		{
			key: 'primary',
			mode: 'primary',
			label: 'Añadir',
			disabled: hasMissingRequired,
			minWidth: '140',
			size: 'size-40',
			onClick() {
				handleSave();
			},
		},
	];

	const onSelectFile = (file: FileUpload) => {
		const isDuplicate = fileDuplicate.onDuplicatedFile({ file, allDocuments });
		if (isDuplicate) return;

		setValue(
			'documento',
			formatDoc({ file, tipoDocumentoMaestro: tipoDocumento, idTipoDocumento: Number(idTipoDocumento) }),
			{
				shouldDirty: true,
				shouldValidate: true,
				shouldTouch: true,
			},
		);
	};

	const onRemoveFile = () => {
		setValue('documento', undefined, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
	};

	// Obtener todos las extensiones disponibles de tipoDocumento y eliminar posibles repeticiones
	const getAllExtensionsAvailables = () => {
		const allExts = tipoDocumento.flatMap(({ extensionesPermitidas }) =>
			extensionesPermitidas?.map(({ extension }) => extension),
		);
		return Array.from(new Set(allExts));
	};

	useEffect(() => {
		let idTipoDocumento;
		if (!hasMultipleOptions) {
			idTipoDocumento = options[0]?.value as string;
		}

		reset({ idTipoDocumento });
	}, [isOpen]);

	useEffect(() => {
		const check = async () => {
			const hasAllRequiredFields = await peekValidation({
				schema: schema({
					globalValues: watchGlobal(),
					maxSizeAllDoc: configuracionTramite?.pesoMaximoSolicitud ?? 0,
				}),
				data: { documento, idTipoDocumento },
				fields: ['documento', 'idTipoDocumento'],
			});

			setHasMissingRequired(!hasAllRequiredFields);
		};

		check();
	}, [documento, idTipoDocumento, schema]);

	const configUpload = useMemo(
		() =>
			getConfigUpload({
				idTipoDocumento: Number(idTipoDocumento ?? 0),
				tipoDocumentoMaestros: tipoDocumento,
				value: documento,
			}),
		[tipoDocumento, documento, idTipoDocumento],
	);

	return (
		<CTNModal
			autoHeight
			closeable
			type="basic"
			title={title}
			size="md"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<form>
					<CTNDropdown
						required
						displayOnly={!hasMultipleOptions}
						name="idTipoDocumento"
						label="Tipo de documento"
						options={options}
						control={control}
						appendTo="body"
						error={errors.idTipoDocumento?.message}
						onChange={() => {
							if (documento) {
								refFile.current?.resetUpload();
								onRemoveFile();
							}
						}}
					/>
					<CTNUpload
						{...commonUpload}
						ref={refFile}
						required
						fileLoaded={configUpload.fileLoaded}
						validExtensions={configUpload.validExtensions ?? getAllExtensionsAvailables()}
						disabled={!configUpload.validExtensions || configUpload.validExtensions.length === 0}
						maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
						label={hasMultipleOptions ? 'Documento' : availablesDocumentos[0]?.descripcion}
						error={fileDuplicate.message ?? errors.documento?.message}
						onRemoveFile={() => onRemoveFile()}
						onSelectFile={onSelectFile}
					/>
				</form>
			}
			onClose={closeModal}
		/>
	);
};

export default ModalAddDocumento;
